import { useEffect, useState, useCallback, useMemo } from "react";
import { Sparkles, Loader2, Info, Save, AlertTriangle, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import AmortizationMiniChart from "@/components/AmortizationMiniChart";

const fmtEur = (n) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
};

// -------------------------------------------------------------------
// Live-Spiegel von backend/kfw_calc.compute_kfw458 (Rechtsstand ab 21.07.2026,
// SHK-Info-Modell): gestaffelter Einkommensbonus, dynamischer WE-Cap,
// Klima-Bonus je Auswahl kommt vom Backend (`klima_by_status`: Heizungsart + Baujahr).
// -------------------------------------------------------------------
const KFW_CAP_PCT = 70.0;
const KFW_GRUND = 30.0;
const KFW_LEGAL_DATE = "21.07.2026";
const EINK_PCT = { bis30k: 40, bis40k: 30, bis50k: 10, ueber50k: 0 };
const STUFEN_ORDER = ["bis30k", "bis40k", "bis50k", "ueber50k"];

// Cap-Formel: base_1we (aus Backend live berechnet, aktuell 28.000 €, sinkt
// halbjährlich −758 € ab 01.02.2027) + 15k je WE 2–6 + 8k je WE ab 7.
const bemessungsCap = (we, base_1we) => {
  const n = Math.max(1, Math.min(14, Number(we) || 1));
  const extra_2_6 = Math.min(Math.max(0, n - 1), 5) * 15000;
  const extra_7_plus = Math.max(0, n - 6) * 8000;
  return (Number(base_1we) || 28000) + extra_2_6 + extra_7_plus;
};

const computeLocal = (s, offerGross, base_1we, klima_pct_current, klimaByStatus) => {
  const stufe = s?.zve_stufe && STUFEN_ORDER.includes(s.zve_stufe) ? s.zve_stufe : "ueber50k";
  const einkommen_full = EINK_PCT[stufe];
  const klimaOn = klimaByStatus && s?.alte_heizung_status in klimaByStatus
    ? klimaByStatus[s.alte_heizung_status]
    : s?.alte_heizung_status === "alt_entsorgt";
  const klima_full = klimaOn ? (Number(klima_pct_current) || 16) : 0;
  const selbstgenutzt = s?.mindestens_eine_we_selbstgenutzt !== false; // Default true
  // Effektive Boni nur für selbstgenutzte WE.
  const einkommen = selbstgenutzt ? einkommen_full : 0;
  const klima = selbstgenutzt ? klima_full : 0;
  // Sonderregel: bei zvE bis 30k hebt sich die Kappungsgrenze auf 80 %
  const cap_pct = stufe === "bis30k" ? 80.0 : KFW_CAP_PCT;
  const total_owner = Math.min(cap_pct, KFW_GRUND + klima + einkommen);
  const total_rental = Math.min(cap_pct, KFW_GRUND);
  const we = Math.max(1, Math.min(14, Number(s?.wohneinheiten) || 1));
  const cap_1we = Number(base_1we) || 28000;
  const capForIdx = (i) => (i === 0 ? cap_1we : (i < 6 ? 15000 : 8000));
  const manuellKosten = Number(s?.kosten_heizungstechnik) || 0;
  const kostenInput = manuellKosten > 0 ? manuellKosten : (Number(offerGross) || 0);
  const kostenProWe = kostenInput / we;
  let eligible_total = 0;
  let subsidy_total = 0;
  let owner_count = 0, rental_count = 0;
  for (let i = 0; i < we; i++) {
    const eligible_i = Math.min(kostenProWe, capForIdx(i));
    const is_owner = selbstgenutzt && i === 0;
    const rate = is_owner ? total_owner : total_rental;
    eligible_total += eligible_i;
    subsidy_total += eligible_i * rate / 100;
    if (is_owner) owner_count++; else rental_count++;
  }
  eligible_total = Math.round(eligible_total * 100) / 100;
  subsidy_total = Math.round(subsidy_total * 100) / 100;
  const eigenanteil = Math.max(0, Math.round((kostenInput - subsidy_total) * 100) / 100);
  const effective_pct = eligible_total > 0
    ? Math.round(subsidy_total / eligible_total * 1000) / 10
    : 0;
  return {
    grundfoerderung_pct: KFW_GRUND,
    klima_pct: klima,
    einkommen_pct: einkommen,
    effizienz_pct: 0,
    total_pct: Math.round(total_owner * 10) / 10,
    effective_total_pct: effective_pct,
    eligible_costs: eligible_total,
    eligible_costs_cap: bemessungsCap(we, base_1we),
    foerderbetrag: subsidy_total,
    eigenanteil,
    legal_date: KFW_LEGAL_DATE,
    kosten_input: kostenInput,
    mindestens_eine_we_selbstgenutzt: selbstgenutzt,
    we_breakdown: {
      owner_count, rental_count,
      owner_rate_pct: Math.round(total_owner * 10) / 10,
      rental_rate_pct: Math.round(total_rental * 10) / 10,
    },
  };
};

// Labels für den Einkommens-Slider — nutzt die Server-übermittelten aktuellen
// Schwellen (sinken um 4.000 €/Jahr ab 01.02.2027). Kind-Toggle verschiebt
// die Labels zusätzlich um +10.000 €.
const stufenLabels = (schwellen, hatKind) => {
  const offset = hatKind ? 10000 : 0;
  const b30 = (schwellen?.bis30k ?? 30000) + offset;
  const b40 = (schwellen?.bis40k ?? 40000) + offset;
  const b50 = (schwellen?.bis50k ?? 50000) + offset;
  return {
    bis30k:   `bis ${fmtEur(b30)}`,
    bis40k:   `bis ${fmtEur(b40)}`,
    bis50k:   `bis ${fmtEur(b50)}`,
    ueber50k: `über ${fmtEur(b50)}`,
  };
};

const HEIZUNG_STATUS_OPTS = [
  { value: "alt_entsorgt",  label: "Älter als 20 Jahre – wird entsorgt",     klima: true  },
  { value: "alt_bleibt",    label: "Älter als 20 Jahre – wird weiter genutzt", klima: false },
  { value: "jung_entsorgt", label: "Jünger als 20 Jahre – wird entsorgt",     klima: false },
  { value: "jung_bleibt",   label: "Jünger als 20 Jahre – wird weiter genutzt", klima: false },
  { value: "defekt",        label: "Defekt – muss ersetzt werden",             klima: false },
];

export default function Kfw458Block({ projectId, previewGross, onSaved }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);
  const [amortRefreshKey, setAmortRefreshKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/projects/${projectId}/kfw458`);
      setData(r.data);
      const s = r.data.settings || {};
      setPending({
        zve_stufe: s.zve_stufe || "ueber50k",
        hat_minderjaehriges_kind: !!s.hat_minderjaehriges_kind,
        wohneinheiten: Number(s.wohneinheiten) || 1,
        kosten_heizungstechnik: Number(s.kosten_heizungstechnik) || 0,
        alte_heizung_status: s.alte_heizung_status || "alt_entsorgt",
        // Iter89l — Default `true`: alte Datensätze verhalten sich weiter
        // wie ein selbstgenutztes Einfamilienhaus (WE=1).
        mindestens_eine_we_selbstgenutzt: s.mindestens_eine_we_selbstgenutzt !== false,
        notes: s.notes || "",
      });
    } catch {
      toast.error("KfW-Daten konnten nicht geladen werden");
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...pending,
        klima_geschwindigkeitsbonus: !!(data?.klima_by_status || {})[pending.alte_heizung_status],
        einkommensbonus: pending.zve_stufe !== "ueber50k",
        effizienzbonus: false,
        // Iter89l — explizit mit senden (Backend hat Default true).
        mindestens_eine_we_selbstgenutzt: pending.mindestens_eine_we_selbstgenutzt !== false,
      };
      const r = await api.patch(`/projects/${projectId}/kfw458`, payload);
      setData(r.data);
      const n = (r.data.refreshed_offers || 0) + (r.data.refreshed_orientation_offers || 0);
      toast.success(n > 0
        ? `Förderung gespeichert · ${n} ${n === 1 ? "Angebot" : "Angebote"} inkl. PDF neu berechnet`
        : "Förderung gespeichert");
      setAmortRefreshKey((k) => k + 1);
      // Iter88zbu — Auto-Reload läuft jetzt global via API-Interceptor (nach 2xx-PATCH).
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const previewN = Number(previewGross) || 0;
  // Iteration 84 → 88f → 88i (2026-02-19): Wahl der Basis für die
  // KfW-458-Bemessungsgrundlage. Historie der bekannten Bugs:
  //   • Carlo Leibeck (07/2026): `previewN || gross_price_base` liess
  //     Zusatzpositionen aus der Bemessungsgrundlage fallen.
  //   • DSS (02/2026 – ver 1): `gross_price_base || previewN` blieb bei
  //     einem stalen gespeicherten Wert hängen, obwohl der Paket-Wechsel
  //     live einen aktuelleren Preis anzeigte.
  //   • DSS (02/2026 – ver 2): `max(saved, live)` half nicht, wenn der
  //     Endkundenpreis (Marge angewandt) live NIEDRIGER ist als der
  //     stale Wholesale-gross in der DB.
  //
  // Definitive Regel (88i):
  //   1. Wenn der User im WP-Konfigurator ein Paket wählt (live-Preis > 0):
  //      → **Live gewinnt IMMER**. Der Frontend-Preis ist das Ground-Truth,
  //        was der Kunde am Ende zahlt. Zusatzpositionen kommen live ins
  //        `totalGross` sobald der Editor sie enthält.
  //   2. Nur wenn kein Paket-Preview vorliegt (z.B. beim Rein-Öffnen des
  //      Projekts ohne Editor-Interaktion): fallback auf `gross_price_base`
  //      aus der letzten gespeicherten Angebotssumme.
  //
  // Damit ist der Bug behoben, dass eine stale DB-Zahl den Förderrechner
  // dominiert und der Slider nicht auf den aktuell gewählten Paket-Preis
  // reagiert.
  const savedGross = Number(data?.gross_price_base) || 0;
  const kostenBasisAusAngebot = previewN > 0 ? previewN : savedGross;
  const c = useMemo(() => {
    if (!pending) return null;
    const base = pending.kosten_heizungstechnik > 0 ? 0 : kostenBasisAusAngebot;
    return computeLocal(pending, base, data?.reduction_info?.current_cap_1we, data?.reduction_info?.current_klima_pct, data?.klima_by_status);
  }, [pending, kostenBasisAusAngebot, data]);

  // Broadcast the live eigenanteil so sibling components (e.g. the big
  // WirtschaftlichkeitBlock further down on the project page) can react to
  // changes in the KfW form without waiting for a save.
  useEffect(() => {
    if (!c || !projectId) return;
    window.dispatchEvent(new CustomEvent("novatus:kfw-preview-change", {
      detail: { projectId, eigenanteil: c.eigenanteil, foerderbetrag: c.foerderbetrag },
    }));
  }, [c, projectId]);

  const dirty = useMemo(() => {
    if (!pending || !data?.settings) return false;
    const s = data.settings;
    return (
      pending.zve_stufe !== (s.zve_stufe || "ueber50k") ||
      pending.hat_minderjaehriges_kind !== !!s.hat_minderjaehriges_kind ||
      Number(pending.wohneinheiten) !== (Number(s.wohneinheiten) || 1) ||
      Number(pending.kosten_heizungstechnik) !== (Number(s.kosten_heizungstechnik) || 0) ||
      pending.alte_heizung_status !== (s.alte_heizung_status || "alt_bleibt") ||
      pending.mindestens_eine_we_selbstgenutzt !== (s.mindestens_eine_we_selbstgenutzt !== false) ||
      (pending.notes || "") !== (s.notes || "")
    );
  }, [pending, data]);

  if (loading) return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 text-zinc-500 text-sm flex items-center gap-2">
      <Loader2 className="animate-spin" size={14} /> Lade KfW-458…
    </div>
  );
  if (!data || !pending || !c) return null;

  const cap = c.eligible_costs_cap;
  const kostenAnzeige = pending.kosten_heizungstechnik > 0 ? pending.kosten_heizungstechnik : kostenBasisAusAngebot;
  const kostenIstManuell = pending.kosten_heizungstechnik > 0;
  // Iter88i: Da die Live-Preview jetzt IMMER als Basis dominiert wenn ein
  // Paket im Konfigurator gewählt wurde, sind Zusatzpositionen bereits im
  // `previewN` enthalten (sofern der User sie hinzugefügt hat). Ein
  // separater Zusatz-Delta ist damit obsolet.
  const zusatzPositionenBrutto = 0;
  const reduction = data.reduction_info || {};
  const labels = stufenLabels(reduction.current_einkommen_schwellen, pending.hat_minderjaehriges_kind);
  const stufeAnzeige = labels[pending.zve_stufe];

  return (
    <div className="bg-gradient-to-br from-[#0F1E18] to-[#0F0F12] border border-[#00E575]/25 rounded-2xl p-5 space-y-5" data-testid="kfw458-block">
      <div className="flex items-start gap-3">
        <div className="h-11 w-11 rounded-xl bg-[#00E575]/15 border border-[#00E575]/40 flex items-center justify-center flex-shrink-0">
          <Sparkles size={20} className="text-[#00E575]" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-base font-bold leading-tight">KfW 458 – Heizungsförderung</h3>
          <p className="text-[11.5px] text-emerald-300 font-semibold tracking-wider mt-0.5">RECHTSSTAND {KFW_LEGAL_DATE} · SHK-INFO-MODELL</p>
        </div>
      </div>

      {/* ── HINWEIS ── */}
      <details className="bg-red-500/5 border border-red-500/30 rounded-xl">
        <summary className="cursor-pointer p-3 flex items-center gap-2 text-[12.5px] font-bold text-rose-200 select-none">
          <AlertTriangle size={14} /> Hinweis zum Förderrechner (unverbindlich)
        </summary>
        <div className="px-3 pb-3 space-y-2 text-[11.5px] text-zinc-200 leading-relaxed">
          <p>Die Berechnung basiert auf dem <b>Informationsstand vom {KFW_LEGAL_DATE}</b> (veröffentlichte Kommunikationen der KfW, BMWE und BWP).</p>
          <p>Der Förderrechner dient ausschließlich einer <b>unverbindlichen Orientierung</b> und ersetzt keine individuelle Förderberatung oder verbindliche Förderzusage.</p>
          <p>Die endgültige Förderrichtlinie ist zum aktuellen Zeitpunkt <b>noch nicht veröffentlicht</b>. Einzelne Fördervoraussetzungen, Fördersätze, Einkommensgrenzen sowie Verfahrensabläufe können sich noch ändern. Wir übernehmen keine Gewähr für Vollständigkeit, Aktualität oder Verbindlichkeit.</p>
          <p className="text-zinc-400">Maßgeblich sind ausschließlich die jeweils gültigen Förderrichtlinien sowie die offiziellen Veröffentlichungen der zuständigen Förderstellen.</p>
        </div>
      </details>

      {/* Iter88ze (2026-02-19) — Sparpotenzial-Hinweis:
          Zeigt konkret welche Bonus-Bausteine noch fehlen und wie viele
          Prozentpunkte im Angebot ungenutzt bleiben. Ziel: Rep sieht auf
          einen Blick warum der Kunde aktuell nur 30 % statt möglicher
          46-80 % bekommt und was er nachpflegen muss. */}
      {(() => {
        const _cap = Number(reduction.current_cap_1we ? (data?.calculation?.cap_pct || 70) : 70);
        const _cur = Number(c?.total_pct || 0);
        const _diff = Math.max(0, _cap - _cur);
        const _missing = [];
        const _klimaOpt = HEIZUNG_STATUS_OPTS.find((o) => (data?.klima_by_status || {})[o.value] && o.value !== pending.alte_heizung_status);
        if (!c?.klima_pct && _klimaOpt) {
          _missing.push(`Klimabonus ${reduction.current_klima_pct ?? 16} % — „${_klimaOpt.label}" wählen`);
        }
        if (!c?.einkommen_pct && (!pending.zve_stufe || pending.zve_stufe === "ueber50k")) {
          _missing.push('Einkommensbonus bis 40 % — zvE-Stufe unten setzen (bis 50 000 € p. a.)');
        }
        if (_missing.length === 0 || _diff < 5) return null;
        return (
          <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-xl p-3.5" data-testid="kfw-savings-hint">
            <div className="flex items-start gap-2.5">
              <Sparkles size={18} className="text-amber-300 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-bold text-amber-100 leading-tight">
                  Bis zu <span className="text-amber-300">+{_diff.toFixed(0)} % zusätzliche Förderung</span> möglich
                </p>
                <ul className="mt-1.5 space-y-0.5 text-[11.5px] text-amber-100/90 leading-snug">
                  {_missing.map((m, i) => (<li key={i}>• {m}</li>))}
                </ul>
                <p className="mt-1.5 text-[10.5px] text-amber-200/70 italic">
                  Sobald die Angaben ergänzt sind, aktualisiert sich das Angebots-PDF automatisch.
                </p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Alte Heizung (Klima-Bonus) ── */}
      <section>
        <label className="block text-[12px] uppercase tracking-wider text-zinc-100 mb-1.5 font-semibold">Zustand der bestehenden Heizung</label>
        <select
          value={pending.alte_heizung_status}
          onChange={(e) => setPending({ ...pending, alte_heizung_status: e.target.value })}
          data-testid="kfw-alte-heizung"
          className="w-full h-11 bg-[#09090B] border-2 border-zinc-600 rounded-xl px-3 text-[13px] text-zinc-50 font-semibold focus:border-[#00E575] outline-none">
          {HEIZUNG_STATUS_OPTS.map(o => (
            <option key={o.value} value={o.value}>{o.label}{(data?.klima_by_status ? data.klima_by_status[o.value] : o.klima) ? ` · +${(reduction.current_klima_pct ?? 16)} % Klima-Bonus` : ""}</option>
          ))}
        </select>
        {c.klima_pct > 0 && reduction.next_klima_reduction && (
          <p className="text-[12px] text-amber-300 font-semibold mt-1">
            Klima-Bonus derzeit <b className="text-emerald-300">{reduction.current_klima_pct} %</b> · nächste Kürzung: <b>{fmtDate(reduction.next_klima_reduction)}</b> (−{reduction.next_klima_reduction_pct} %/Halbjahr)
          </p>
        )}
        {/* Iteration 85 (2026-02-16): BEG-Alterssperre — wenn die Bestandsheizung
            (Gas/Öl/Kohle/Nachtspeicher) unter dem Mindestalter von 20 Jahren
            liegt, muss der Klima-Bonus rechtlich auf 0 gesetzt werden. Der
            Backend-Calc erledigt das automatisch; hier zeigen wir den Grund an. */}
        {(data?.klima_lock_reason_by_status || {})[pending.alte_heizung_status] && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/40 text-rose-200 text-[12px] font-semibold" data-testid="kfw-klima-age-lock">
            <b className="text-rose-100">Klima-Bonus rechtlich gesperrt.</b><br/>
            {data.klima_lock_reason_by_status[pending.alte_heizung_status]}
            <div className="text-[11px] text-rose-300 font-medium mt-0.5">
              Heizungsart und Baujahr im Verbrauchs-Reiter prüfen bzw. korrigieren.
            </div>
          </div>
        )}
      </section>

      {/* ── Kind + Einkommens-Slider ── */}
      <section className="space-y-3">
        <label className="flex items-center gap-2.5 cursor-pointer select-none">
          <input type="checkbox" checked={pending.hat_minderjaehriges_kind}
            onChange={(e) => setPending({ ...pending, hat_minderjaehriges_kind: e.target.checked })}
            data-testid="kfw-kind-toggle"
            className="accent-[#00E575] w-5 h-5" />
          <span className="text-[14px] font-bold text-zinc-50">Mindestens ein minderjähriges Kind wohnt im Haushalt</span>
        </label>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[12px] uppercase tracking-wider text-zinc-100 font-semibold">Zu versteuerndes Haushaltseinkommen</label>
            <span className="text-[15px] font-semibold text-zinc-100" data-testid="kfw-zve-label">{stufeAnzeige}</span>
          </div>
          <input type="range" min="0" max="3" step="1"
            value={STUFEN_ORDER.indexOf(pending.zve_stufe)}
            onChange={(e) => setPending({ ...pending, zve_stufe: STUFEN_ORDER[Number(e.target.value)] })}
            data-testid="kfw-zve-slider"
            style={{ "--slider-fill": `${(STUFEN_ORDER.indexOf(pending.zve_stufe) / 3) * 100}%` }}
            className="w-full novatus-slider" />
          <div className="flex justify-between text-[12px] font-medium text-zinc-300 mt-2 px-0.5">
            <span>{labels.bis30k}</span>
            <span>{labels.bis40k}</span>
            <span>{labels.bis50k}</span>
            <span>{labels.ueber50k}</span>
          </div>
          <p className="text-[12px] text-zinc-100 font-semibold mt-1.5">
            Einkommens-Bonus: <b className="text-emerald-300">+{c.einkommen_pct} %</b>
            {pending.hat_minderjaehriges_kind && <span className="text-zinc-300"> · Grenzen +10.000 € durch Kind</span>}
          </p>
          {reduction.next_einkommen_reduction && (
            <p className="text-[12px] text-amber-300 font-semibold mt-1">
              Nächste Kürzung der Einkommensgrenzen: <b>{fmtDate(reduction.next_einkommen_reduction)}</b> (−{fmtEur(reduction.next_einkommen_reduction_amount)} pro Stufe/Jahr)
            </p>
          )}
        </div>
      </section>

      {/* ── Wohneinheiten ── */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[12px] uppercase tracking-wider text-zinc-100 font-semibold">Wohneinheiten im Gebäude</label>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPending({ ...pending, wohneinheiten: Math.max(1, (pending.wohneinheiten || 1) - 1) })}
              data-testid="kfw-we-minus" className="h-7 w-7 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center">
              <Minus size={12} />
            </button>
            <span className="font-mono text-sm font-bold text-zinc-100 w-6 text-center" data-testid="kfw-we-value">{pending.wohneinheiten}</span>
            <button type="button" onClick={() => setPending({ ...pending, wohneinheiten: Math.min(14, (pending.wohneinheiten || 1) + 1) })}
              data-testid="kfw-we-plus" className="h-7 w-7 rounded-lg bg-zinc-800 hover:bg-zinc-700 inline-flex items-center justify-center">
              <Plus size={12} />
            </button>
          </div>
        </div>
        <input type="range" min="1" max="14" step="1" value={pending.wohneinheiten}
          onChange={(e) => setPending({ ...pending, wohneinheiten: Number(e.target.value) })}
          data-testid="kfw-we-slider"
          style={{ "--slider-fill": `${((pending.wohneinheiten - 1) / 13) * 100}%` }}
          className="w-full novatus-slider" />
        <p className="text-[12px] text-zinc-100 font-semibold mt-1">
          Förderfähige Bemessungsgrundlage (Cap): <b className="text-emerald-300">{fmtEur(cap)}</b>
        </p>
        <p className="text-[12px] text-zinc-300 font-medium mt-0.5">
          Aktueller 1-WE-Cap: <b className="text-zinc-100">{fmtEur(reduction.current_cap_1we || 28000)}</b> · +15.000 € je WE 2–6 · +8.000 € je WE ab 7
        </p>
        {reduction.next_cap_reduction && (
          <p className="text-[12px] text-amber-300 font-semibold mt-1">
            Nächste Halbjahres-Kürzung: <b>{fmtDate(reduction.next_cap_reduction)}</b> (−{fmtEur(reduction.next_cap_reduction_amount)})
          </p>
        )}
      </section>

      {/* Iter89l (2026-02-21) — Wohnungsnutzung: bei WEG/MFH ohne
          Selbstnutzung entfällt der Klima- und Einkommens-Bonus für alle
          WE. Der Sales-Rep MUSS diesen Toggle bei Mehrfamilien­häusern
          aktiv prüfen. Referenz-Rechner (Screenshot Christof 21.02.2026)
          hat exakt diese Checkbox („Mind. eine Wohneinheit ist selbst
          genutzt"). */}
      <section data-testid="kfw-wohnungsnutzung">
        <label className="text-[12px] uppercase tracking-wider text-zinc-100 font-semibold block mb-2">
          Wohnungsnutzung *
        </label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setPending({ ...pending, mindestens_eine_we_selbstgenutzt: true })}
            data-testid="kfw-nutzung-selbst"
            className={`rounded-xl p-3 border-2 text-left transition ${
              pending.mindestens_eine_we_selbstgenutzt
                ? "bg-[#00E575]/12 border-[#00E575] text-emerald-100"
                : "bg-[#0F0F12] border-zinc-800 text-zinc-300 hover:border-zinc-600"
            }`}
          >
            <div className="text-[13px] font-semibold leading-tight">Selbst genutzt</div>
            <div className="text-[11.5px] text-zinc-400 mt-0.5 leading-snug">
              Mind. 1 WE wird vom Eigentümer bewohnt · Klima- & Einkommens-Bonus greifen für diese WE
            </div>
          </button>
          <button
            type="button"
            onClick={() => setPending({ ...pending, mindestens_eine_we_selbstgenutzt: false })}
            data-testid="kfw-nutzung-vermietet"
            className={`rounded-xl p-3 border-2 text-left transition ${
              !pending.mindestens_eine_we_selbstgenutzt
                ? "bg-amber-500/12 border-amber-400 text-amber-100"
                : "bg-[#0F0F12] border-zinc-800 text-zinc-300 hover:border-zinc-600"
            }`}
          >
            <div className="text-[13px] font-semibold leading-tight">Nicht selbst genutzt</div>
            <div className="text-[11.5px] text-zinc-400 mt-0.5 leading-snug">
              Alle WE sind vermietet · nur 30 % Grundförderung, kein Klima-/Einkommens-Bonus
            </div>
          </button>
        </div>
        {pending.wohneinheiten > 1 && (
          <p className="text-[11.5px] text-zinc-300 mt-2 leading-snug">
            {pending.mindestens_eine_we_selbstgenutzt ? (
              <>Aufteilung: <b className="text-emerald-300">1 × WE @ {c.we_breakdown?.owner_rate_pct} %</b> + <b className="text-zinc-100">{pending.wohneinheiten - 1} × WE @ {c.we_breakdown?.rental_rate_pct} %</b> (Grundförderung)</>
            ) : (
              <>Alle <b>{pending.wohneinheiten}</b> Wohnungen erhalten nur die <b className="text-amber-300">Grundförderung 30 %</b>.</>
            )}
          </p>
        )}
      </section>

      {/* ── Kosten Heizungstechnik ── */}
      <section>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[12px] uppercase tracking-wider text-zinc-100 font-semibold flex items-center gap-1">
            Kosten Heizungstechnik
            {!kostenIstManuell && (
              <span
                className="cursor-help text-zinc-400 hover:text-emerald-300 transition"
                title="Grundlage der KfW-458-Berechnung ist der GESAMTE Angebotspreis (Brutto) — inklusive aller Zusatzpositionen wie Speicher, Umfeldmaßnahmen und Zubehör. Die 28.000-€-Cap (bzw. + 15.000 € je WE 2–6, + 8.000 € je WE ab 7) begrenzt die förderfähigen Kosten automatisch."
                data-testid="kfw-kosten-info"
              >
                <Info size={12} />
              </span>
            )}
          </label>
          <span className="text-[14px] font-semibold text-zinc-50" data-testid="kfw-kosten-value">
            {fmtEur(kostenAnzeige)}
            {!kostenIstManuell && <span className="text-[12px] text-zinc-300 font-semibold ml-1">(aus Angebot, gesamt)</span>}
          </span>
        </div>
        {!kostenIstManuell && zusatzPositionenBrutto > 0 && (
          <div className="text-[12px] text-zinc-300 mb-1.5 leading-snug" data-testid="kfw-kosten-explanation">
            Ausgewähltes Paket {fmtEur(previewN)} · Zusatzpositionen im Angebot {fmtEur(zusatzPositionenBrutto)} · Cap {fmtEur(cap)}
          </div>
        )}
        <input type="range" min="0" max={cap} step="500"
          value={Math.min(kostenAnzeige, cap)}
          onChange={(e) => setPending({ ...pending, kosten_heizungstechnik: Number(e.target.value) })}
          data-testid="kfw-kosten-slider"
          style={{ "--slider-fill": `${cap > 0 ? (Math.min(kostenAnzeige, cap) / cap) * 100 : 0}%` }}
          className="w-full novatus-slider" />
        {kostenIstManuell && (
          <button type="button" onClick={() => setPending({ ...pending, kosten_heizungstechnik: 0 })}
            data-testid="kfw-kosten-reset"
            className="text-[12px] text-emerald-300 font-semibold hover:underline mt-1">
            → Auf Angebots-Preis zurücksetzen ({fmtEur(kostenBasisAusAngebot)})
          </button>
        )}
      </section>

      {/* ── Live-Rechnung ── */}
      <div className="bg-[#09090B] border border-zinc-800 rounded-xl p-4 space-y-1.5">
        <RowKv label="Grundförderung" value="30 %" />
        <RowKv label={`Klima-Bonus${!pending.mindestens_eine_we_selbstgenutzt ? " (entfällt · vermietet)" : ""}`} value={`${c.klima_pct} %`} />
        <RowKv label={`Einkommens-Bonus${!pending.mindestens_eine_we_selbstgenutzt ? " (entfällt · vermietet)" : ""}`} value={`${c.einkommen_pct} %`} />
        <RowKv label={`Förderquote${pending.mindestens_eine_we_selbstgenutzt && pending.wohneinheiten > 1 ? " selbstgenutzte WE" : ""}${pending.zve_stufe === "bis30k" ? " (Cap 80 %)" : " (Cap 70 %)"}`} value={`${c.total_pct} %`} highlight />
        {pending.wohneinheiten > 1 && pending.mindestens_eine_we_selbstgenutzt && (
          <RowKv label={`Effektive Gesamt-Förderquote (${pending.wohneinheiten} WE gemischt)`} value={`${c.effective_total_pct ?? c.total_pct} %`} />
        )}
        <div className="border-t border-zinc-800 my-2" />
        <RowKv label={`Anzurechnende Kosten (max. ${fmtEur(c.eligible_costs_cap)})`} value={fmtEur(c.eligible_costs)} />
        <RowKv label="Förderzuschuss KfW 458" value={`-${fmtEur(c.foerderbetrag)}`} green />
        <div className="border-t border-zinc-800 my-2" />
        <RowKv label="Ihr Eigenanteil" value={fmtEur(c.eigenanteil)} big />
      </div>

      {/* ── 12-Monats-Impact-Tabelle (Zeitfenster) ── */}
      {reduction.current_cap_1we && (() => {
        const in12_cap_1we = Math.max(0, (reduction.current_cap_1we || 28000) - 2 * 750); // 2 Halbjahre in 12 Mo
        const in12_klima = c.klima_pct > 0 ? Math.max(0, (reduction.current_klima_pct || 16) - 8) : 0;
        const in12_cap_full = in12_cap_1we + Math.min(Math.max(0, pending.wohneinheiten - 1), 5) * 15000 + Math.max(0, pending.wohneinheiten - 6) * 8000;
        const cap_pct = pending.zve_stufe === "bis30k" ? 80 : 70;
        const in12_total = Math.min(cap_pct, 30 + in12_klima + c.einkommen_pct);
        const in12_eligible = Math.max(0, Math.min(c.kosten_input, in12_cap_full));
        const in12_foerder = Math.round(in12_eligible * in12_total) / 100;
        const delta = Math.round((c.foerderbetrag - in12_foerder) * 100) / 100;
        if (delta <= 0.5) return null;
        return (
          <div className="bg-red-500/5 border border-red-500/40 rounded-xl p-4" data-testid="kfw-impact-panel">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle size={16} className="text-rose-300 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-[13px] font-semibold text-rose-200 leading-tight">Zeitfenster: Bei 12 Monaten Verzögerung gehen bis zu {fmtEur(delta)} verloren</h4>
                <p className="text-[12px] text-zinc-300 font-semibold mt-0.5">Klima-Bonus & Bemessungsgrundlage sinken automatisch ab 01.02.2027</p>
              </div>
            </div>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[12px] text-zinc-300 font-semibold border-b border-zinc-700">
                  <th className="text-left py-2"></th>
                  <th className="text-right py-2">Antrag heute</th>
                  <th className="text-right py-2">In 12 Monaten</th>
                </tr>
              </thead>
              <tbody className="text-zinc-100 font-medium">
                <tr className="border-b border-zinc-800/60"><td className="py-1.5">Klima-Bonus</td><td className="text-right text-emerald-300 font-mono">{c.klima_pct} %</td><td className="text-right text-rose-300 font-mono">{in12_klima} %</td></tr>
                <tr className="border-b border-zinc-800/60"><td className="py-1.5">Bemessungsgrundlage 1. WE</td><td className="text-right text-emerald-300 font-mono">{fmtEur(reduction.current_cap_1we)}</td><td className="text-right text-rose-300 font-mono">{fmtEur(in12_cap_1we)}</td></tr>
                <tr className="border-b border-zinc-800/60"><td className="py-1.5">Bemessungsgrundlage gesamt</td><td className="text-right text-emerald-300 font-mono">{fmtEur(c.eligible_costs_cap)}</td><td className="text-right text-rose-300 font-mono">{fmtEur(in12_cap_full)}</td></tr>
                <tr className="border-b border-zinc-800/60"><td className="py-1.5">Gesamt-Förderquote</td><td className="text-right text-emerald-300 font-mono">{c.total_pct} %</td><td className="text-right text-rose-300 font-mono">{in12_total.toFixed(1)} %</td></tr>
                <tr><td className="py-2 font-semibold">Ihr Zuschuss KfW 458</td><td className="text-right text-emerald-300 font-mono font-semibold text-[15px]">{fmtEur(c.foerderbetrag)}</td><td className="text-right text-rose-300 font-mono font-semibold text-[15px]">{fmtEur(in12_foerder)}</td></tr>
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Eingebetteter Amortisations-Mini-Chart (ROI-Auswirkung der Förderung live sichtbar) */}
      <AmortizationMiniChart
        projectId={projectId}
        previewGross={previewN || Number(data.gross_price_base) || 0}
        previewEigenanteil={c.eigenanteil}
        refreshKey={amortRefreshKey}
        onJumpToDetail={() => document.querySelector('[data-anchor="amortisation"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
      />

      {/* Notes */}
      <div>
        <label className="block text-[12px] uppercase tracking-wider text-zinc-100 mb-1.5 font-semibold">Interne Notizen</label>
        <textarea rows={2} value={pending.notes || ""}
          onChange={(e) => setPending({ ...pending, notes: e.target.value })}
          data-testid="kfw-notes"
          placeholder="z. B. Selbstnutzer bestätigt · Kessel Bj. 1998 · …"
          className="w-full bg-[#09090B] border border-zinc-700 rounded-xl p-2.5 text-sm text-zinc-100 focus:border-[#00E575] outline-none resize-y" />
      </div>

      <button onClick={save} disabled={saving} data-testid="kfw-save-btn"
        className="h-11 w-full rounded-xl bg-[#00E575] text-[#09090B] font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#00C964] flex items-center justify-center gap-2">
        {saving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} /> {dirty ? "Förderung speichern" : "Förderung speichern & Angebote aktualisieren"}</>}
      </button>

      <p className="text-[12px] text-zinc-200 font-semibold flex items-start gap-1.5 leading-relaxed">
        <Info size={12} className="text-zinc-200 mt-0.5 flex-shrink-0" />
        Unverbindliche Vorabschätzung nach KfW-458 Rechtsstand {KFW_LEGAL_DATE}. Effizienzbonus (R290) ist ab diesem Datum entfallen. Tatsächlicher Anspruch hängt vom Antrag, Eigentumsverhältnis und der Prüfung der KfW ab.
      </p>
    </div>
  );
}

function RowKv({ label, value, highlight, green, big }) {
  return (
    <div className="flex items-center justify-between gap-2 py-0.5">
      <span className={`text-[12.5px] ${highlight ? "text-zinc-100 font-semibold" : "text-zinc-300"}`}>{label}</span>
      <span className={`font-mono ${big ? "text-xl font-display font-semibold text-emerald-300" : green ? "text-emerald-300 font-semibold text-[14px]" : highlight ? "text-zinc-100 font-semibold text-[13px]" : "text-zinc-200 text-[13px]"}`}>{value}</span>
    </div>
  );
}
